import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

interface WinnerEventData {
  id: number;
  machineCode: string;
  winnerWallet: string;
  prizeName: string;
  prizeImage: string | null;
  insuredValue: number;
  isLegendary: boolean;
  gapFromPrevious: number | null;
  timestamp: string;
}

interface InventoryUpdateData {
  machineCode: string;
  type: "item-won" | "sync-complete";
  itemName?: string;
  itemValue?: number;
  timestamp: string;
}

interface UseRealtimeEventsOptions {
  machineCode?: string | null;
  onWinnerEvent?: (data: WinnerEventData) => void;
  onLegendaryWin?: (data: WinnerEventData) => void;
  onStatsUpdate?: (data: unknown) => void;
  onInventoryUpdate?: (data: InventoryUpdateData) => void;
  enabled?: boolean;
}

const API_BASE = import.meta.env.VITE_API_URL ?? `${window.location.origin}/api`;

// Delay before invalidating queries to allow ably-listener to process the event
const INVALIDATION_DELAY_MS = 500;

// Minimum spacing between refetch batches. Pulls can arrive multiple times per
// second and each SSE event used to force a refetch of every stats query in
// every open tab; coalescing keeps the counters live (≤2s behind) without the
// per-pull request stampede. The live feed itself is unaffected — it renders
// straight from the SSE payload via the on* callbacks.
const INVALIDATION_THROTTLE_MS = 2000;

export function useRealtimeEvents({
  machineCode,
  onWinnerEvent,
  onLegendaryWin,
  onStatsUpdate,
  onInventoryUpdate,
  enabled = true,
}: UseRealtimeEventsOptions = {}) {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const pendingInvalidationsRef = useRef<Set<string>>(new Set());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFlushRef = useRef(0);

  // Queue query invalidations and flush them in coalesced batches: the first
  // event flushes after INVALIDATION_DELAY_MS (so the db write completes),
  // and further events piggyback on the pending flush, spaced at least
  // INVALIDATION_THROTTLE_MS apart. Keys are deduplicated across events.
  const invalidateWithDelay = useCallback(
    (queryKeys: string[][]) => {
      for (const queryKey of queryKeys) {
        pendingInvalidationsRef.current.add(JSON.stringify(queryKey));
      }
      if (flushTimerRef.current) return; // pending flush will pick these up
      const sinceLastFlush = Date.now() - lastFlushRef.current;
      const wait = Math.max(
        INVALIDATION_DELAY_MS,
        INVALIDATION_THROTTLE_MS - sinceLastFlush
      );
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        lastFlushRef.current = Date.now();
        const keys = Array.from(pendingInvalidationsRef.current);
        pendingInvalidationsRef.current.clear();
        for (const key of keys) {
          queryClient.invalidateQueries({
            queryKey: JSON.parse(key),
            refetchType: "all", // Force refetch even if not stale
          });
        }
      }, wait);
    },
    [queryClient]
  );

  const connect = useCallback(() => {
    if (!enabled) return;

    // Build URL with optional machine filter
    // Use window.location.origin as base for relative URLs (e.g., "/api")
    const baseUrl = API_BASE.startsWith("http")
      ? API_BASE
      : `${window.location.origin}${API_BASE}`;
    const url = new URL(`${baseUrl}/stream/events`);
    if (machineCode) {
      url.searchParams.set("machine", machineCode);
    }

    console.log("[SSE] Connecting to:", url.toString());

    const eventSource = new EventSource(url.toString());
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      console.log("[SSE] Connected");
      reconnectAttempts.current = 0;
    };

    eventSource.onerror = (error) => {
      console.error("[SSE] Error:", error);
      eventSource.close();
      eventSourceRef.current = null;

      // Exponential backoff reconnect
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
      reconnectAttempts.current++;

      console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
      reconnectTimeoutRef.current = setTimeout(connect, delay);
    };

    // Handle connection confirmation
    eventSource.addEventListener("connected", (event) => {
      console.log("[SSE] Connection confirmed:", JSON.parse(event.data));
    });

    // Handle winner events - every pull triggers this
    eventSource.addEventListener("winner-event", (event) => {
      const data = JSON.parse(event.data) as WinnerEventData;
      console.log("[SSE] Winner event:", data.prizeName, `($${data.insuredValue})`);

      // Invalidate queries after a delay to allow db write to complete
      invalidateWithDelay([
        ["machine-stats", data.machineCode],
        ["machine-gaps", data.machineCode],
        ["machine-gaps-all-tiers", data.machineCode],
        ["tiered-events", data.machineCode],
      ]);

      onWinnerEvent?.(data);
    });

    // Handle legendary/tier wins - only high value prizes
    eventSource.addEventListener("legendary-win", (event) => {
      const data = JSON.parse(event.data) as WinnerEventData;
      console.log("[SSE] Tier win:", data.prizeName, `($${data.insuredValue})`);

      // Invalidate queries after a delay
      invalidateWithDelay([
        ["machine-stats", data.machineCode],
        ["machine-gaps", data.machineCode],
        ["machine-gaps-all-tiers", data.machineCode],
        ["tiered-events", data.machineCode],
        ["leaderboard"],
      ]);

      onLegendaryWin?.(data);
    });

    // Handle stats updates
    eventSource.addEventListener("stats-update", (event) => {
      const data = JSON.parse(event.data);
      console.log("[SSE] Stats update");

      // Invalidate stats queries
      invalidateWithDelay([["machine-stats"]]);

      onStatsUpdate?.(data);
    });

    // Handle inventory updates
    eventSource.addEventListener("inventory-updated", (event) => {
      const data = JSON.parse(event.data) as InventoryUpdateData;
      console.log("[SSE] Inventory update:", data.type, data.itemName ?? "");

      // Invalidate inventory queries after a delay
      invalidateWithDelay([
        ["inventory", data.machineCode],
        ["inventory-changes", data.machineCode],
      ]);

      onInventoryUpdate?.(data);
    });

    // Handle keepalive (just to confirm connection is alive)
    eventSource.addEventListener("keepalive", () => {
      // Connection is alive, nothing to do
    });
  }, [enabled, machineCode, invalidateWithDelay, onWinnerEvent, onLegendaryWin, onStatsUpdate, onInventoryUpdate]);

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        console.log("[SSE] Disconnecting");
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
        pendingInvalidationsRef.current.clear();
      }
    };
  }, [connect]);

  return {
    isConnected: eventSourceRef.current?.readyState === EventSource.OPEN,
  };
}
